/**
 * DataTable — table primitive with sort + search + extensible toolbar.
 *
 * Wraps TanStack Table's headless engine in a project-styled shell so feature
 * code only declares columns + data. Layout: toolbar (search left, filter
 * chips left, optional actions right), then table, then empty / loading
 * states.
 *
 * Filter chips are consumer-controlled — pass any nodes via the `filterChips`
 * slot and pre-filter `data` accordingly. Search filtering is internal: pass
 * a `searchAccessor` to control which fields the search input matches.
 *
 * To build columns, use TanStack's `createColumnHelper` (re-exported from
 * here as a convenience).
 */

import { useState, type ReactNode } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { t } from '@/lib/i18n';
import styles from './DataTable.module.css';

export { createColumnHelper } from '@tanstack/react-table';
export type { ColumnDef } from '@tanstack/react-table';

export interface DataTableProps<TData> {
  /** Row data. Pre-filtered by any chip-style filters before being passed in. */
  data: readonly TData[];
  columns: ColumnDef<TData, unknown>[];

  /** When provided, renders a search input; matches against `searchAccessor(row)`. */
  searchPlaceholder?: string;
  /** Returns the string value(s) to match the search input against. Defaults to row JSON. */
  searchAccessor?: (row: TData) => string;

  /** Chip-style filters rendered in the toolbar — Chip components, buttons, anything. */
  filterChips?: ReactNode;

  /** Second row of filters rendered below the primary toolbar row. */
  subFilterChips?: ReactNode;

  /** Right-aligned toolbar slot — typically a primary "+ New" button. */
  toolbarActions?: ReactNode;

  /** Spinner-style fallback (string for v1). */
  isLoading?: boolean;
  /** Rendered when `data` is empty (post-filter). */
  emptyState?: ReactNode;

  /** Accessible name for the table element. */
  ariaLabel?: string;

  /** Initial sort state. Users can still click headers to change it. */
  defaultSort?: { id: string; desc?: boolean };
}

export function DataTable<TData>({
  data,
  columns,
  searchPlaceholder,
  searchAccessor,
  filterChips,
  subFilterChips,
  toolbarActions,
  isLoading = false,
  emptyState,
  ariaLabel,
  defaultSort,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(
    defaultSort ? [{ id: defaultSort.id, desc: defaultSort.desc ?? false }] : [],
  );
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable<TData>({
    data: data as TData[],
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: searchAccessor
      ? (row, _columnId, value) => {
          const haystack = searchAccessor(row.original).toLowerCase();
          return haystack.includes(String(value).toLowerCase());
        }
      : 'includesString',
  });

  const showToolbar = Boolean(searchPlaceholder ?? filterChips ?? subFilterChips ?? toolbarActions);
  const rows = table.getRowModel().rows;

  return (
    <div className={styles.root}>
      {showToolbar ? (
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {searchPlaceholder ? (
              <input
                type="search"
                className={styles.search}
                placeholder={searchPlaceholder}
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                aria-label={searchPlaceholder}
              />
            ) : null}
            {filterChips ? <div className={styles.filters}>{filterChips}</div> : null}
          </div>
          {toolbarActions ? <div className={styles.actions}>{toolbarActions}</div> : null}
          {subFilterChips ? <div className={styles.toolbarSecondary}>{subFilterChips}</div> : null}
        </div>
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table} aria-label={ariaLabel}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        sortDir === 'asc'
                          ? 'ascending'
                          : sortDir === 'desc'
                            ? 'descending'
                            : undefined
                      }
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          className={styles.sortBtn}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          <span className={styles.sortArrow} aria-hidden="true">
                            {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '⇅'}
                          </span>
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className={styles.stateCell}>
                  {t('Loading…')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.stateCell}>
                  {emptyState ?? t('No results.')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
